import { UserEntity } from 'app/interfaces/entity';
import { sessionSelector } from 'app/store/session/selectors';
import config from 'config/config';
import keycloak from 'keycloak';
import { createChatUser } from 'modules/chat/utils';
import React, { createContext, FC, useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import Talk from 'talkjs';

interface PlayerContextProps {
  session: Talk.Session | null;
  talkLoaded: boolean;
  unreadConversationsCount: number;
}

export const ChatSessionContext = createContext<PlayerContextProps>({
  session: null,
  talkLoaded: false,
  unreadConversationsCount: 0,
});

export const ChatSessionProvider: FC = ({ children }) => {
  const [talkLoaded, markTalkLoaded] = useState(false);
  const [session, setSession] = useState<Talk.Session | null>(null);
  const [unreadConversationsCount, setUnreadConversationsCount] = useState(0);
  const currentUser = useSelector(sessionSelector.activeUser) as UserEntity;

  const unreadConversationsHandler = useCallback(
    (unreadConversations) => setUnreadConversationsCount(unreadConversations.length),
    [setUnreadConversationsCount]
  );

  useEffect(() => {
    Talk.ready.then(() => markTalkLoaded(true));
    if (talkLoaded && currentUser && currentUser.hasConversations && keycloak?.authenticated) {
      const me = createChatUser(currentUser);
      if (me) {
        const newSession = new Talk.Session({
          appId: config.talkJsAppId,
          me,
          signature: currentUser.chat.signature,
        });
        setSession(newSession);
      }
    }
  }, [talkLoaded, currentUser]);

  useEffect(() => {
    if (session) {
      session.unreads?.onChange(unreadConversationsHandler);
    }

    return () => session?.destroy();
  }, [session, unreadConversationsHandler]);

  return (
    <ChatSessionContext.Provider value={{ session, talkLoaded, unreadConversationsCount }}>
      {children}
    </ChatSessionContext.Provider>
  );
};
